import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, useMemo } from 'react';
import { useAuth } from './useAuthSimple.jsx';
import { getToken, setToken, setSuperadminToken, setSuperadminRememberMe, getTokenRememberMe } from '@/utils/storage';
import unifiedApi from '../services/unifiedApi';
import { API_BASE_URL } from '../config/api.config.js';

const superadminFetch = async (path, params) => {
  const r = await unifiedApi.get(`/superadmin${path}`, params ? { params } : undefined);
  if (!r.success) throw new Error(r.message || 'Erreur API superadmin');
  return r.data;
};

function computeHealthScore(school, engagementData) {
  let score = 0;

  // GPS freshness (0–40 pts)
  if (school.gps_last_signal) {
    const ageMin = (Date.now() - new Date(school.gps_last_signal).getTime()) / 60000;
    score += ageMin < 15 ? 40 : ageMin < 60 ? 20 : ageMin < 360 ? 5 : 0;
  }

  // Admin last login (0–30 pts)
  if (school.admin_last_login) {
    const ageDays = (Date.now() - new Date(school.admin_last_login).getTime()) / 86400000;
    score += ageDays < 7 ? 30 : ageDays < 30 ? 15 : ageDays < 90 ? 5 : 0;
  }

  // Parent activation rate (0–20 pts)
  const eng = engagementData.find((e) => e.school_id === school.id);
  if (eng && eng.total > 0) {
    const rate = eng.activated / eng.total;
    score += rate >= 0.7 ? 20 : rate >= 0.4 ? 10 : rate >= 0.2 ? 5 : 0;
  }

  // Plan headroom (0–10 pts) — penalise si proche de la limite
  const sPct = school.usage?.students?.max ? school.usage.students.count / school.usage.students.max : 0;
  score += sPct < 0.9 ? 10 : sPct < 1.0 ? 5 : 0;

  return Math.min(100, score);
}

export const useSuperadmin = () => {
  const queryClient = useQueryClient();
  const { logout } = useAuth();

  const overview = useQuery({
    queryKey: ['superadmin', 'overview'],
    queryFn: () => superadminFetch('/stats/overview'),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  const schoolsUsage = useQuery({
    queryKey: ['superadmin', 'schools-usage'],
    queryFn: () => superadminFetch('/schools/usage'),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  const alerts = useQuery({
    queryKey: ['superadmin', 'alerts'],
    queryFn: () => superadminFetch('/alerts'),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  const gpsHealth = useQuery({
    queryKey: ['superadmin', 'gps-health'],
    queryFn: () => superadminFetch('/gps/health'),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  const [trendDays, setTrendDays] = useState(30);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [schoolFilter, setSchoolFilter] = useState({ query: '', tier: 'all', status: 'all' });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  const trends = useQuery({
    queryKey: ['superadmin', 'trends', trendDays],
    queryFn: () => superadminFetch('/trends', { days: trendDays }),
    staleTime: 60_000,
  });

  const gpsPositions = useQuery({
    queryKey: ['superadmin', 'gps-positions'],
    queryFn: () => superadminFetch('/gps/positions'),
    staleTime: 15_000,
    refetchInterval: 30_000,
  });

  const engagement = useQuery({
    queryKey: ['superadmin', 'engagement'],
    queryFn: () => superadminFetch('/parents/engagement'),
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });

  const schoolsRaw = schoolsUsage.data ?? [];
  const engagementData = engagement.data ?? [];

  // Enrich each school with a computed health score
  const schools = useMemo(
    () => schoolsRaw.map((s) => ({ ...s, healthScore: computeHealthScore(s, engagementData) })),
    [schoolsRaw, engagementData],
  );

  // Global derived metrics (computed from already-loaded data — no extra fetch)
  const globalActivationRate = useMemo(() => {
    if (!engagementData.length) return null;
    const total = engagementData.reduce((s, e) => s + (e.total ?? 0), 0);
    const activated = engagementData.reduce((s, e) => s + (e.activated ?? 0), 0);
    return total > 0 ? Math.round((activated / total) * 100) : null;
  }, [engagementData]);

  const atRiskCount = useMemo(
    () => schools.filter((s) => s.healthScore < 50 && s.status === 'active').length,
    [schools],
  );

  const handleCreateSchool = async (payload) => {
    const r = await unifiedApi.post('/superadmin/schools', payload);
    if (!r.success) throw new Error(r.message || 'Erreur création école');
    queryClient.invalidateQueries({ queryKey: ['superadmin', 'schools-usage'] });
    queryClient.invalidateQueries({ queryKey: ['superadmin', 'overview'] });
    return r.data;
  };

  const handlePatchSchool = async (schoolId, patch) => {
    const r = await unifiedApi.patch(`/superadmin/schools/${schoolId}`, patch);
    if (!r.success) throw new Error(r.message || 'Erreur mise à jour école');
    queryClient.invalidateQueries({ queryKey: ['superadmin', 'schools-usage'] });
    queryClient.invalidateQueries({ queryKey: ['superadmin', 'overview'] });
    queryClient.invalidateQueries({ queryKey: ['superadmin', 'alerts'] });
    return r.data;
  };

  const handleDeleteGpsPositions = async ({ beforeDate, schoolId = null }) => {
    const body = { before_date: beforeDate };
    if (schoolId) body.school_id = schoolId;
    const r = await unifiedApi.delete('/superadmin/gps/cleanup', { data: body });
    if (!r.success) throw new Error(r.message || 'Erreur lors de la suppression GPS');
    queryClient.invalidateQueries({ queryKey: ['superadmin', 'overview'] });
    return r.data;
  };

  const handleExportGpsPositions = async ({ schoolId, fromDate, toDate, schoolName }) => {
    const token = getToken();
    const params = new URLSearchParams({ school_id: schoolId });
    if (fromDate) params.set('from_date', fromDate);
    if (toDate) params.set('to_date', toDate);
    const res = await fetch(`${API_BASE_URL}/superadmin/gps/export?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Export GPS échoué');
    const truncated = res.headers.get('X-Truncated') === 'true';
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    const safeName = (schoolName || schoolId).replace(/[^a-z0-9_-]/gi, '_');
    const dateTag = (fromDate || 'all') + '_' + (toDate || 'all');
    a.download = `positions_${safeName}_${dateTag}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    return { truncated };
  };

  const handleImpersonate = async (schoolId) => {
    const r = await unifiedApi.post(`/superadmin/impersonate/${schoolId}`);
    if (!r.success) throw new Error(r.message || 'Erreur impersonate');
    const rememberMe = getTokenRememberMe();
    const currentToken = getToken();
    if (currentToken) setSuperadminToken(currentToken);
    setSuperadminRememberMe(rememberMe);
    setToken(r.data.token, rememberMe);
    window.location.href = '/admin/tracking';
  };

  const handleGetDeletionPreview = async (schoolId) => {
    const r = await unifiedApi.get(`/superadmin/schools/${schoolId}/deletion-preview`);
    if (!r.success) throw new Error(r.message || 'Erreur chargement du récapitulatif de suppression');
    return r.data;
  };

  const handleDeleteSchool = async (schoolId, confirmName) => {
    const r = await unifiedApi.delete(`/superadmin/schools/${schoolId}`, { data: { confirm_name: confirmName } });
    if (!r.success) throw new Error(r.message || 'Erreur lors de la suppression définitive');
    queryClient.invalidateQueries({ queryKey: ['superadmin', 'overview'] });
    queryClient.invalidateQueries({ queryKey: ['superadmin', 'schools-usage'] });
    return r.data;
  };

  return {
    overview: overview.data ?? null,
    overviewLoading: overview.isLoading,
    schools,
    schoolsLoading: schoolsUsage.isLoading,
    alerts: alerts.data ?? [],
    alertsLoading: alerts.isLoading,
    gpsHealth: gpsHealth.data ?? null,
    gpsHealthLoading: gpsHealth.isLoading,
    gpsPositions: gpsPositions.data ?? [],
    gpsPositionsLoading: gpsPositions.isLoading,
    engagement: engagementData,
    engagementLoading: engagement.isLoading,
    schoolFilter,
    setSchoolFilter,
    showCreateModal,
    setShowCreateModal,
    handleCreateSchool,
    trends: trends.data ?? null,
    trendsLoading: trends.isLoading,
    trendDays,
    setTrendDays,
    selectedSchool,
    setSelectedSchool,
    handlePatchSchool,
    handleImpersonate,
    handleGetDeletionPreview,
    handleDeleteSchool,
    handleDeleteGpsPositions,
    handleExportGpsPositions,
    logout,
    activeTab,
    setActiveTab,
    globalActivationRate,
    atRiskCount,
    error: overview.error?.message ?? schoolsUsage.error?.message ?? null,
  };
};
